/**
 * The backend rejects a time entry it considers implausible, and each rejection
 * has a specific reason the person registering can act on. Without a match here
 * the toast falls back to a generic "could not save" — or, through
 * `useOnErrorAuto`, to the literal string "Server error" — which tells them
 * nothing and turns into a support ticket.
 *
 * Matched on the message text, following the existing `Comment is required`
 * handling. Keep these in step with `timesheets/services/time_entry_span.py`
 * and `timesheets/schema.py::_validate_time_entry_comment_required`.
 *
 * Kept free of `@ur-solutions/react-components` imports so it stays unit
 * testable — that barrel cannot be loaded under vitest. The hook that shows the
 * toast lives in `time-entry-errors.hooks.ts`.
 */
export const TIME_ENTRY_ERROR_MATCHERS = [
  {
    /** timesheets/schema.py — ValueError raised on an overlapping range */
    text: 'Dates are overlapping with already saved date range',
    type: 'warning' as const,
    key: 'overlapping' as const,
  },
  {
    /** timesheets/schema.py::_validate_time_entry_comment_required */
    text: 'Comment is required',
    type: 'error' as const,
    key: 'commentRequired' as const,
  },
  {
    /** time_entry_span.validate_time_entry_span — inverted chronology */
    text: 'A time entry must end after it starts',
    type: 'error' as const,
    key: 'endBeforeStart' as const,
  },
  {
    /** time_entry_span.validate_time_entry_span — over the maximum work day */
    text: 'A time entry cannot be longer than',
    type: 'error' as const,
    key: 'tooLong' as const,
  },
]

export type TimeEntryErrorMatch = (typeof TIME_ENTRY_ERROR_MATCHERS)[number]

/** The rejection a backend message describes, or `null` when it is not one of ours. */
export function matchTimeEntryError(
  message: string | undefined | null
): TimeEntryErrorMatch | null {
  if (!message) return null

  return (
    TIME_ENTRY_ERROR_MATCHERS.find(matcher => message.includes(matcher.text)) ??
    null
  )
}
